// noinspection TypeScriptValidateJSTypes

import * as React from "react";
import { styled } from "@mui/material";
import { usePatientFeatureFlags } from "../../../hooks/usePatientFeatureFlags";
import { usePatient } from "../../../state/patientContext";
import {
  ReferencesBox,
  ReferencesBoxText,
  ReferencesButton,
  ReferencesButtonContainer,
  MobileReasoningTopArea,
  MobileReasoningTopTitle,
} from "./mobileSuggestionReasoningBox";
import { useTranslate } from "../../../hooks/useTranslate";
import { PatientColorMap } from "../../../layout/patientColorMap";
import { PatientChartFindingMap } from "fe-web-components/dialogs/ZoomWidgets";
import {
  DemoZoomContent,
  DemoZoomGraphButtonSupport,
} from "./useDemoReasoningController";
import { useEffect, useMemo, useState } from "react";
import { GenericButton } from "../../uiControllers/genericButton";
import { ShowGraphButton } from "./showGraphButton";

//region [[ Styles ]]

const MobileDiseaseReasoningBoxView = styled("div")({});

const ReasoningList = styled("div")({
  display: "flex",
  flexFlow: "column",
  justifyContent: "flex-start",
  alignItems: "flex-start",
});

const ReasoningListTitle = styled("div")({
  width: "100%",
  display: "flex",
  flexFlow: "row",
  padding: "0 5px",
  borderBottom: `1px solid ${PatientColorMap.DefaultDark}`,
  justifyContent: "flex-end",
  alignItems: "center",
});

const ReasoningListContent = styled("div")({
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  width: "100%",
  "& .generic-button": {
    width: "30%",
  },
});

const Reasoning = styled("div")({
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
  justifyContent: "center",
  flexBasis: "30%",
  margin: "10px 0",
});

const ReasoningText = styled("span")({
  "&.green": { color: "green" },
  "&.red": { color: "red" },
});

const Finding = styled(PatientChartFindingMap)({
  flexBasis: "50%",
  "& .patient-chart-finding": {
    paddingRight: "initial",
  },
  "& .refinement-container .finding-refinements-item": {
    marginLeft: "15px",
    color: PatientColorMap.DefaultDark,
    fontSize: "11px",
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface MobileDiseaseReasoningBoxProps
  extends DemoZoomContent,
    DemoZoomGraphButtonSupport {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]
const DEFAULT_DESKTOP_ITEMS_COUNT_TO_SHOW = 7;
export const MobileDiseaseReasoningBox = ({
  setTitle,
  desktop,
  currentSelectedDisease,
  ...props
}: MobileDiseaseReasoningBoxProps) => {
  const {
    conversation,
    conversationDDResult,
    selectedDiseaseReasoning,
  } = usePatient();

  const [showAll, setShowAll] = useState(!desktop);

  const { diseaseName: selectedDiseaseName, diseaseId: selectedDiseaseId } =
    currentSelectedDisease || {};

  const {
    suggestions: { diseases },
  } = conversation || { suggestions: {} };

  const diseasesWCrs = diseases?.find(
    (d) => d.disease.id === selectedDiseaseId,
  );

  const shouldUseLikelihoodRatio = usePatientFeatureFlags("UseLRsForStrengths");

  const { translate } = useTranslate();

  let crs = null;

  if (diseasesWCrs) {
    crs = diseasesWCrs.crFindings
      ?.filter(
        (cr) =>
          (shouldUseLikelihoodRatio && cr.likelihoodRatio) ||
          (!shouldUseLikelihoodRatio && cr.absolutePredictiveValue !== 1),
      )
      ?.sort((a, b) => {
        if (shouldUseLikelihoodRatio) {
          return (b.likelihoodRatio || 1) - (a.likelihoodRatio || 1);
        } else {
          return b.absolutePredictiveValue - a.absolutePredictiveValue;
        }
      });
  } else if (selectedDiseaseReasoning) {
    crs = [
      ...conversationDDResult.ddClusters.top,
      ...conversationDDResult.ddClusters.others,
    ].find((cluster) => cluster.title.id === selectedDiseaseReasoning.diseaseId)
      ?.cr?.findings;
  }

  const diseaseName = useMemo(
    () => selectedDiseaseName.replace("(disorder)", ""),
    [selectedDiseaseName],
  );

  const title = useMemo(
    () =>
      `${translate("WHY_IS_THIS")} ${diseaseName} ${translate(
        "CONSIDERED_NOW",
      )}?`,
    [diseaseName, translate],
  );

  useEffect(() => {
    if (desktop && setTitle) setTitle(title);
  }, [desktop, setTitle, title]);

  if (!crs) return <>No crs for disease</>;

  const descriptionText = translate("DISEASE_REASONING_EXPLANATION_TEXT")
    .split("#DISEASE_NAME#")
    .join(diseaseName);

  return (
    <MobileDiseaseReasoningBoxView>
      {!desktop && (
        <MobileReasoningTopArea className={"top-area"}>
          <MobileReasoningTopTitle className={"top-title"}>
            {title}
          </MobileReasoningTopTitle>
        </MobileReasoningTopArea>
      )}
      <ReferencesBox className={"references-box"}>
        <ReferencesBoxText>{descriptionText}</ReferencesBoxText>
        <ReferencesButtonContainer>
          {!desktop && (
            <ReferencesButton
              label={translate("SHOW_REFERENCES")}
              onClick={props.onReferencesClick}
            />
          )}
        </ReferencesButtonContainer>
      </ReferencesBox>
      <ReasoningList>
        <ReasoningListTitle>
          <Reasoning>+/- Likelihood ratio</Reasoning>
        </ReasoningListTitle>
        <ReasoningListContent>
          {crs
            .slice(
              0,
              showAll ? crs.length : DEFAULT_DESKTOP_ITEMS_COUNT_TO_SHOW,
            )
            .map((cr, i) => (
              <React.Fragment key={i}>
                <Finding qf={cr.finding?.name ? cr.finding : cr.qf} idx={i} />
                <Reasoning>
                  {shouldUseLikelihoodRatio && (
                    <ReasoningText
                      className={cr.likelihoodRatio >= 1 ? "green" : "red"}
                    >
                      {cr.likelihoodRatio?.toFixed(2)}
                    </ReasoningText>
                  )}
                  {!shouldUseLikelihoodRatio && (
                    <ReasoningText
                      className={
                        cr.absolutePredictiveValue > 1 ? "green" : "red"
                      }
                    >
                      {cr.absolutePredictiveValue > 1 ? "+" : ""}
                      {Math.log10(cr.absolutePredictiveValue)?.toFixed(1)}
                    </ReasoningText>
                  )}
                </Reasoning>
                <ShowGraphButton qf={cr.finding} onClick={props.onShowGraph} />
              </React.Fragment>
            ))}
          {desktop && crs.length > DEFAULT_DESKTOP_ITEMS_COUNT_TO_SHOW && (
            <GenericButton
              onClick={() => setShowAll((s) => !s)}
              label={showAll ? "Show Less" : "Show All"}
            />
          )}
        </ReasoningListContent>
      </ReasoningList>
    </MobileDiseaseReasoningBoxView>
  );
};
